/**
 * Who an email deep link was addressed to (R11).
 *
 * Every CTA in a message `services/notify.deliver_email` sent carries two
 * parameters, stamped in one place — `stamp_recipient` in
 * `backend/services/email_service.py`:
 *
 *     /client/jobs/job_53cf60722ddb?for=user_abc123&hint=d%E2%80%A2%E2%80%A2%E2%80%A2%40gmail.com
 *
 * `for` is the account the message went to. `hint` is that account's masked
 * address, and exists only to be printed: this browser knows the session's
 * email and not the recipient's, so without it a mismatch notice could say
 * "another account" and nothing a person could act on.
 *
 * **Neither is authority.** They pick between showing the page and showing a
 * notice; every endpoint the page then calls enforces ownership itself. That is
 * why nothing here is signed and why a malformed value degrades to "no
 * recipient named" rather than to an error — the worst a forged `for` can do is
 * warn someone about an account they already knew the name of.
 */

/** The parameter names `stamp_recipient` writes. Keep the two in step. */
export const RECIPIENT_PARAM = "for";
export const RECIPIENT_HINT_PARAM = "hint";

/** Nothing longer than this is printed. A hint is `d•••@domain`, never prose. */
const MAX_HINT = 120;

/**
 * The shape of a `for=` this module owns (T11 N42).
 *
 * **`for` names two different things in this app and only one of them is a
 * recipient.** R11 stamps `?for=<user_id>` on every email CTA; the directed
 * quote request, older than R11 by ten days, uses `?for=<public_slug>` to say
 * which FixyBee is being invited — `/client/jobs/new?for=busy-bee`. Both pass
 * through `ProtectedRoute`, which read the slug as an account id, compared it
 * to the session's, and answered "Este enlace es para otra cuenta" to a client
 * opening an invitation from a FixyBee's own page. The link was never for
 * another account; it was not naming an account at all.
 *
 * The two namespaces cannot collide, and that is what makes telling them apart
 * a fact rather than a guess: a `user_id` is `user_` plus twelve hex digits
 * (`routes/auth.py`), and `_slugify` in `routes/users.py` replaces every
 * character outside `[a-z0-9]` with a hyphen — so no public slug can contain
 * an underscore, let alone begin with `user_`.
 *
 * Anything that does not match is left to whoever else reads the parameter,
 * which is the existing "this link names nobody" case and the one every
 * in-app navigation already takes.
 */
const RECIPIENT_SHAPE = /^user_[0-9a-f]{6,}$/;

/**
 * `{ userId, hint }` for a link that names its recipient; `userId` is `""`
 * otherwise, which every caller reads as "this link names nobody" — the state
 * of an in-app navigation, of a bookmark, of every link sent before R11, and
 * of a `for=` carrying a FixyBee's public slug rather than an account id
 * (see `RECIPIENT_SHAPE`).
 *
 * Takes `URLSearchParams` or a raw `?a=b` string, for the reason
 * `lib/returnTo.readReturnTo` does: both spellings are in this codebase.
 */
export function readRecipient(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const value = String(params?.get?.(RECIPIENT_PARAM) || "").trim();
  const hint = String(params?.get?.(RECIPIENT_HINT_PARAM) || "").trim();
  const userId = RECIPIENT_SHAPE.test(value) ? value : "";
  return {
    userId: userId.length > MAX_HINT ? "" : userId,
    hint: hint.length > MAX_HINT ? "" : hint,
  };
}

/**
 * True when this link names a recipient and the live session is somebody else.
 *
 * False whenever the question cannot be answered — no recipient named, or no
 * session loaded yet. Both are the ordinary case, and neither is a mismatch.
 */
export function isWrongRecipient(recipient, user) {
  const mine = user?.user_id;
  if (!recipient?.userId || !mine) return false;
  return recipient.userId !== mine;
}

export default readRecipient;
